<template>
  <div id="digital-event-room-video" class="content-panel">
    <div class="content-body">
      <div :class="activeVideoSession ? 'video-other' : 'video-me'">
        <div
          class="video-members"
          :class="[
            activeVideoChat && activeVideoChat.videoChatType === videoChatTypes.ONE_TO_ONE
              ? 'type-one-to-one'
              : activeVideoChat
              ? 'type-many-to-many'
              : '',
            'callers-' + videoChatMembers.length
          ]"
          id="video-chat"
          @error="errorHandler"
        >
          <CallMember v-for="member in videoChatMembers" :key="getMemberKey(member)" :member="member"></CallMember>
          <div v-if="!activeVideoChat && multiVideoChatEnabled" class="multi-party-message">
            Up to {{ maximumVideoChatMembers }} people can join video calls.<br />Start a new call to add others.
          </div>
        </div>
      </div>

      <div class="video-settings-panel">
        <div class="buttons">
          <div class="button-grp buttons-av">
            <button
              type="button"
              class="btn btn-default btn-mic"
              @click="togglePublisherAudio(!publisherConfig.publishAudio)"
            >
              <i
                class="ar-icon"
                :class="publisherConfig.publishAudio ? 'icon-microphone-solid' : 'icon-microphone-slash-solid'"
                aria-hidden="true"
              ></i>
              <br />{{ publisherConfig.publishAudio ? "Mute" : "Unmute" }}
            </button>
            <span :title="videoReasonMessage">
              <button
                type="button"
                class="btn btn-default btn-video"
                :disabled="disableStartVideoButton"
                @click="togglePublisherVideo(!publisherConfig.publishVideo)"
              >
                <i
                  class="ar-icon"
                  :class="publisherConfig.publishVideo ? 'icon-video-solid' : 'icon-video-slash-solid'"
                  aria-hidden="true"
                ></i>
                <br />{{ publisherConfig.publishVideo ? "Stop Video" : "Start Video" }}
              </button>
            </span>
            <button type="button" v-if="!activeVideoChat" class="btn btn-default" @click="showAddUserMW">
              <i class="ar-icon user_in icon-video-light" aria-hidden="true"></i><br />New Call
            </button>
            <button type="button" class="btn btn-default" @click="openCallSettingsModal(false)">
              <i class="ar-icon icon-cog-solid" aria-hidden="true"></i><br />Settings
            </button>
          </div>

          <div class="button-grp buttons-volume">
            <button
              type="button"
              v-if="activeVideoChat"
              class="btn btn-default"
              @click="toggleSubscribersAudio(!subscriberConfig.subscribeToAudio)"
            >
              <i
                v-if="subscriberConfig.subscribeToAudio"
                class="fa fa-fw"
                :class="subscriberConfig.audioVolume > 30 ? 'fa-volume-up' : 'fa-volume-down'"
                aria-hidden="true"
              ></i>
              <i v-else class="fa fa-fw fa-volume-off" aria-hidden="true"></i>
            </button>

            <div v-if="activeVideoChat" class="volume-controls">
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                :value="subscriberConfig.subscribeToAudio ? subscriberConfig.audioVolume : 0"
                @change="changeSubscribersAudioVolume"
              />
            </div>
          </div>

          <div class="button-grp buttons-call">
            <button
              type="button"
              class="btn btn-default"
              v-if="isAllowInvitationsEnabled()"
              :disabled="!canAddMorePeopleOnTheVideoCall"
              @click="showAddUserMW"
            >
              <i class="fa fa-lg fa-user-plus" aria-hidden="true"></i><br />Invite others <br /><span class="muted"
                >Max {{ maximumVideoChatMembers }} per call</span
              >
            </button>
            <button
              type="button"
              v-if="isConnectingToOneToOneVideoChatInProgress()"
              class="btn btn-default"
              @click="cancelVideoCallInvites"
            >
              <i class="ar-icon icon-phone-slash-solid" aria-hidden="true"></i><br />Cancel
            </button>
            <button
              type="button"
              v-if="activeVideoChat && activeVideoSession && !isConnectingToOneToOneVideoChatInProgress()"
              class="btn btn-default"
              @click="leaveVideoCall"
            >
              <i class="ar-icon icon-phone-slash-solid" aria-hidden="true"></i><br />Hang up
            </button>
          </div>
        </div>
        <div v-if="isAllowInvitationsCheckboxVisible()" class="allow-invites">
          <div class="key-node other">
            <label class="styled-cb">
              <span class="filter-name">Allow others to invite to this call</span>
              <input id="allowVideChatInvitations" type="checkbox" v-model="allowInvitations" />
              <span class="checkmark"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OT from "@opentok/client";
import VideoChatMixin from "@/components/DigitalEvent/RoomList/Room/mixins/VideoChatMixin";
import VideoChatPermissionsMixin from "@/components/DigitalEvent/mixins/Permissions/VideoChatPermissionsMixin";
import VideoCallInviteMixin from "@/components/DigitalEvent/RoomList/Room/mixins/Invite/VideoCallInviteMixin";
import WindowWithMixin from "@/components/DigitalEvent/RoomList/Room/mixins/WindowWithMixin";
import UsersMW from "@/components/DigitalEvent/RoomList/Room/VideoChat/UsersMW";
import { VIDEO_MEMBER_TYPES } from "@/helpers/constants";
import { mapState, mapGetters, mapActions } from "vuex";
import VideoSettingsModal from "@/modules/calls/components/VideoSettingsModal";

import CallMember from "@/modules/calls/components/CallMember";

export default {
  name: "VideoChat",
  mixins: [VideoChatMixin, VideoChatPermissionsMixin, VideoCallInviteMixin, WindowWithMixin],
  components: {
    CallMember
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      activeCamera: 0
    };
  },
  methods: {
    ...mapActions({
      toggleVideoChatsFunctionality: "videoChat/toggleVideoChatsFunctionality",
      toggleSubscribersAudio: "calls/setSubscriberAudioEnabled",
      togglePublisherAudio: "calls/setPublisherAudioEnabled",
      togglePublisherVideo: "calls/setPublisherVideoEnabled"
    }),
    /**
     * Create video session
     */
    createSession() {
      let session = OT.initSession(this.activeVideoChat.apiKey, this.activeVideoChat.sessionId);

      session.connect(this.activeVideoChat.token, async error => {
        if (error) {
          this.$store.commit("videoChat/setConnectionsCount", 0);
          this.$store.commit("videoChat/setActiveVideoSession", null);
          await this.$store.dispatch("videoChat/logVideoChatsAction", {
            actionName: "SESSION_CREATED",
            error: error
          });
          this.errorHandler(error);
        } else {
          this.$store.commit("videoChat/setVideoChatMember", {
            userUUID: this.$store.state.users.user.user_uuid,
            data: {
              memberType: VIDEO_MEMBER_TYPES.PUBLISHER,
              invite: null
            }
          });
          this.$store.commit("videoChat/incrementConnectionsCount");
          await this.$store.dispatch("videoChat/logVideoChatsAction", { actionName: "SESSION_CREATED" });
        }
      });

      session.on("sessionConnected", () => {
        this.$store.dispatch("calls/publishToSession", { session });
      });

      session.on("sessionDisconnected", async ({ reason }) => {
        if (reason === "forceDisconnected") {
          await this.$store.dispatch("videoChat/closeActiveVideoSession");
        }
        await this.$store.dispatch("videoChat/logVideoChatsAction", { actionName: "SESSION_DISCONNECTED" });
      });

      session.on("connectionCreated", async event => {
        await this.$store.dispatch("videoChat/processConnectionCreated", event);
        await this.$store.dispatch("videoChat/logVideoChatsAction", { actionName: "CONNECTION_CREATED" });
      });

      session.on("connectionDestroyed", async () => {
        await this.$store.dispatch("videoChat/logVideoChatsAction", { actionName: "CONNECTION_DESTROYED" });
      });

      session.on("streamCreated", async event => {
        await this.$store.dispatch("videoChat/addVideoChatStream", event.stream);
      });

      session.on("streamDestroyed", async event => {
        await this.$store.dispatch("videoChat/deleteVideoChatStream", event.stream);
      });

      session.on("signal", async event => {
        await this.$store.dispatch("videoChat/processSessionSignal", event);
      });

      this.$store.commit("videoChat/setActiveVideoSession", session);
    },

    /** Set subscribers audio streams volume */
    changeSubscribersAudioVolume(event) {
      this.$store.dispatch("calls/setSubscriberAudioLevel", parseInt(event.target.value));
    },
    /** Open video settings modal */
    openCallSettingsModal(runConnectivityTest) {
      this.$modal.show(VideoSettingsModal, {}, { height: "auto" });
      if (runConnectivityTest) {
        this.$store.dispatch("calls/connectivity/performConnectivityTest");
      }
    },
    /**
     * Open ModalWindow for joining users to the current video call
     */
    showAddUserMW(options) {
      this.$modal.show(UsersMW, {}, Object.assign({ height: this.modalHeight, width: this.modalWidth }, options || {}));
    },

    /**
     * Error handler
     * @param err
     */
    errorHandler(err) {
      // eslint-disable-next-line no-console
      console.error(err.message);
    },
    getMemberKey(member) {
      if (member.userUUID === this.$store.state.users.user.user_uuid) {
        return `call-member-SELF-${member.userUUID}`;
      }
      return `call-member-${member.memberType}-${member.userUUID}`;
    }
  },
  computed: {
    ...mapState({
      activeVideoDevice: state => state.videoChat.activeVideoDevice,
      isDevicesReady: state => state.calls.devices.isReady,
      testResultData: state => state.calls.connectivity.testResultData
    }),
    ...mapGetters({
      publicTableUsers: "tables/publicTableUsers",
      user: "users/currentUser",
      subscriberConfig: "calls/subscriberConfig",
      publisherConfig: "calls/publisherConfig"
    }),
    /**
     * Modal width
     * @return {String}
     */
    modalWidth() {
      return this.windowWidth < 768 ? "90%" : "50%";
    },

    /**
     * Modal height
     * @return {String}
     */
    modalHeight() {
      return this.windowWidth < 768 ? "90%" : "70%";
    },
    /**
     * Disable Start Video button if reason exist
     * @return {Boolean}
     */
    disableStartVideoButton() {
      if (!this.testResultData) return true;
      else if (this.testResultData.video.reason) return true;
      else return false;
    },
    /**
     * Reason message
     * @return {String}
     */
    videoReasonMessage() {
      return this.testResultData?.video?.reason ? this.testResultData.video.reason : "";
    }
  },
  created() {
    this.$store.dispatch("videoChat/toggleVideoChatsFunctionality");

    // Init window with variable
    window.addEventListener("resize", this.resizeHandler);
  },
  mounted() {
    if (this.isDevicesReady && !this.$store.state.calls.connectivity.testResultData) {
      this.openCallSettingsModal(true);
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeHandler);
  },
  watch: {
    activeVideoChat(newValue) {
      if (newValue && !this.$store.state.videoChat.activeVideoSession) {
        this.createSession();
      }
    },
    isDevicesReady(isReady) {
      if (isReady && !this.$store.state.calls.connectivity.testResultData) {
        this.openCallSettingsModal(true);
      }
    }
  }
};
</script>
